import { Injectable } from '@angular/core';
import { ColorRecipeModel } from '../edit/models/color-recipe.model';
import { Store } from '@ngxs/store';
import { EngineState } from './state/engine.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { ViewTreeNode } from './models/view.tree-node';
import { EngineService } from './engine.service';
import { PaintPresetOriginal, PaintPresetFade } from './models/paint-presets.const';
import { BdGraph } from 'src/lib/graph/graph.model';
import { get } from 'lodash';
import { ObjectColor } from '../shared/object-color';

declare const Human: any;

@Injectable({
  providedIn: 'root'
})
export class PaintService {

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private engineService: EngineService
  ) { }

  highlightDisabled = false;
      
  colorObject(objectId: string, params: ColorRecipeModel) {
    const humanObject = Human.scene.objects[objectId];
    if (humanObject instanceof Human.scene.Object) {
      // to clear or not to clear, that is the question
      // apply mod properties that are not default
      if (params.hasOwnProperty('tintColor')) {
        humanObject.setTintColor(params.tintColor);
      }
      if (params.hasOwnProperty('saturation')) {
        humanObject.setSaturation(params.saturation);
      }
      if (params.hasOwnProperty('brightness')) {
        humanObject.setBrightness(params.brightness);
      }
      if (params.hasOwnProperty('contrast')) {
        humanObject.setContrast(params.contrast);
      }
      if (params.hasOwnProperty('opacity')) {
        humanObject.setOpacity(params.opacity);
      }
    }
  }

  getColor(objectId: string): ColorRecipeModel {
    return ObjectColor.getColorRecipe(Human, objectId);
  }

  uncolorObject(objectId: string) {
    const object = Human.scene.objects[objectId];
    if (object instanceof Human.scene.Object) {
      object.clearColorModifiers();
      object.clearOpacityModifiers(); 
    }
  }

  unpaint(node: ViewTreeNode) {
    // relay color mods to each engine leaf
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    this.graphService.getLeafDescendants(viewGraph, node.id).forEach((id) => {
      this.engineService.getObjects(id).forEach((humanObjectId: string) => {
        const humanObject = Human.scene.objects[humanObjectId];
        if (humanObject instanceof Human.scene.Object) {
          // reset object mod properties
          humanObject.clearColorModProperties();
          humanObject.clearOpacityModProperties();
        }
      });
    });
  }

  paint(node: ViewTreeNode, recipe: ColorRecipeModel) {
    // relay color mods to each engine leaf
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const engineRecipe = ColorRecipeModel.toEngine(recipe);

    this.graphService.getLeafDescendants(viewGraph, node.id).forEach((id) => {
      this.engineService.getObjects(id).forEach((humanObjectId: string) => {
        this.uncolorObject(humanObjectId);
        this.colorObject(humanObjectId, engineRecipe);
      });
    });
  }

  highlight(node: ViewTreeNode, highlight: boolean) {
    // allow un-highlight if highlight is disabled
    if (this.highlightDisabled === false || highlight === false) { 
      // relay highlight to each engine leaf
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      this.graphService.getLeafDescendants(viewGraph, node.id).forEach((id) => {
        this.engineService.getObjects(id).forEach((humanObjectId: string) => {
          const humanObject = Human.scene.objects[humanObjectId];
          if (humanObject instanceof Human.scene.Object) {
            humanObject.setHighlight(highlight);
          }
        });
      });
    }
  }

  xRay(node: ViewTreeNode, xRay: boolean) {
    const object = Human.scene.objects[node.id];
    object.setXRay(xRay);
  }

  paintAll(nodes: ViewTreeNode[], recipe: ColorRecipeModel) {
    nodes.forEach(node => {
      this.paint(node, recipe);
    });
  }

  highlightAll(nodes: ViewTreeNode[], highlight: boolean) {
    nodes.forEach(node => {
      this.highlight(node, highlight);
    });
  }

  inverseXRay(nodes: ViewTreeNode[], viewGraph: BdGraph<ViewTreeNode>) {
    const nodeIds = nodes.map(node => node.id);

    const inverseNodeIds = this.graphService.getInverseSelection(viewGraph, nodeIds);
    const inverseNodes = this.graphService.getNodes(viewGraph, inverseNodeIds);

    this.paintAll(inverseNodes, PaintPresetFade.recipe);
    this.paintAll(nodes, PaintPresetOriginal.recipe);
  }

  disableHighlight() {
    this.highlightDisabled = true;
  }

  enableHighlight() {
    this.highlightDisabled = false;
  }

}
